<div class="modal-overlay" data-testid="upload-modal" *ngIf="isOpen" (click)="onCancel()">
  <div class="modal-container" (click)="$event.stopPropagation()">
    <!-- Uploading overlay -->
    <div *ngIf="isUploading" class="uploading-overlay">
      <div class="uploading-content">
        <app-spinner [display]="true" [diameter]="48" aria-label="Uploading"></app-spinner>
        <span class="uploading-text">Uploading document…</span>
      </div>
    </div>

    <div class="modal-header">
      <h2 class="modal-title">
        {{ showAffectedEntities() ? 'Affected Entities' : 'Document Upload' }}
      </h2>
      <button
        class="close-button"
        data-testid="upload-modal-close-btn"
        (click)="onCancel()"
        aria-label="Close modal"
      >
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>

    <!-- Step 1: Replacement file upload -->
    <div
      class="modal-content overlay-scroll--custom"
      scrollActivity
      [scrollActivityCustom]="true"
      *ngIf="currentStep() === 1"
    >
      <div class="modal-content-inner">
        <p class="modal-description">Please upload the file for the necessary document update.</p>

        <div class="upload-wrapper">
          <div
            *ngIf="!selectedFile || errorMessage"
            class="upload-zone"
            data-testid="upload-drop-zone"
            [class.upload-zone--dragging]="isDragging"
            [class.upload-zone--error]="errorMessage"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)"
            (click)="fileInput.click()"
          >
            <span class="material-symbols-outlined upload-icon">upload_file</span>
            <p class="upload-text">
              Drag and drop file here or
              <label class="file-label" (click)="$event.stopPropagation()">
                <span class="choose-file-text">Choose File</span>
                <input
                  #fileInput
                  tabindex="0"
                  type="file"
                  class="file-input"
                  data-testid="upload-file-input"
                  [accept]="acceptedFormats"
                  (change)="onFileSelected($event)"
                />
              </label>
            </p>
          </div>

          <div class="error-message" data-testid="upload-error-message" *ngIf="errorMessage">
            <span class="material-symbols-outlined">error</span>
            <span>{{ errorMessage }}</span>
          </div>

          <div
            class="selected-file"
            data-testid="upload-selected-file"
            *ngIf="selectedFile && !errorMessage"
          >
            <div class="file-info">
              <span class="material-symbols-outlined file-icon">description</span>
              <div class="file-details">
                <span class="file-name">{{ selectedFileName }}</span>
                <span class="file-size">{{ selectedFileSize }}</span>
              </div>
            </div>
            <button
              class="remove-file-btn"
              data-testid="upload-remove-file-btn"
              (click)="removeFile()"
              type="button"
            >
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="upload-info">
            <span class="upload-info-text">Supported formats: {{ supportedFormatsText }}</span>
            <span class="upload-info-text">Maximum size: 10MB</span>
          </div>
        </div>

        <div
          class="form-group"
          data-testid="upload-file-type-section"
          *ngIf="shouldShowDocumentTypeSelector"
        >
          <app-az-selector
            data-testid="upload-file-type-dropdown"
            [label]="'Uploaded File'"
            [options]="documentTypes"
            [selectedValue]="selectedDocumentType"
            [disabled]="disableDocumentTypeSelector"
            (selectionChange)="onDocumentTypeChange($event)"
          ></app-az-selector>
        </div>

        <div class="form-group" *ngIf="showCommentBox">
          <span class="form-label">Comments:</span>
          <textarea
            class="form-textarea"
            data-testid="upload-comments-textarea"
            [(ngModel)]="comments"
            rows="4"
          ></textarea>
        </div>

        <div class="modal-actions">
          <app-az-button
            data-testid="upload-cancel-btn"
            [label]="'Cancel Upload'"
            [showIcon]="false"
            (click)="onCancel()"
            variant="default"
            [disabled]="isUploading"
          ></app-az-button>
          <app-az-button
            data-testid="upload-confirm-btn"
            [label]="'Confirm Upload'"
            [showIcon]="false"
            (click)="onConfirm()"
            variant="primary"
            [disabled]="!selectedFile || !selectedDocumentType || isUploading"
          ></app-az-button>
        </div>
      </div>
    </div>

    <!-- Step 2: full-width incomplete contacts banner (outside scroll area, flush with modal edges) -->
    <div
      *ngIf="currentStep() === 2 && !showAffectedEntities() && incompleteContacts.length > 0"
      class="modal-warning"
      data-testid="bcr-173-incomplete-contacts-banner"
    >
      <span class="material-symbols-outlined modal-warning__icon">warning</span>
      <p class="modal-warning__message">
        {{ incompleteContactsCountLabel }}
        {{ incompleteContacts.length === 1 ? 'legal entity has' : 'legal entities have' }}
        incomplete contact details. Review the
        <button
          type="button"
          class="modal-warning__link"
          data-testid="bcr-173-affected-entities-link"
          (click)="showAffectedEntitiesPanel()"
        >
          affected entities
        </button>
        before proceeding.
      </p>
    </div>

    <!-- Step 2: Adobe signing PDF upload + recipient selection -->
    <div
      class="modal-content overlay-scroll--custom"
      scrollActivity
      [scrollActivityCustom]="true"
      data-testid="bcr-173-notification-step"
      *ngIf="currentStep() === 2"
    >
      <!-- Affected Entities detail panel -->
      <div
        *ngIf="showAffectedEntities()"
        class="affected-entities-panel"
        data-testid="bcr-173-affected-entities-panel"
      >
        <button
          type="button"
          class="affected-entities-back-link"
          data-testid="bcr-173-affected-entities-back-btn"
          (click)="hideAffectedEntitiesPanel()"
        >
          <span class="material-symbols-outlined">chevron_left</span>
          Back to Document Upload
        </button>
        <p class="affected-entities-description">
          The following legal entities are missing important contact information:
        </p>
        <ul class="affected-entities-list">
          <li *ngFor="let contact of incompleteContacts">
            {{ contact.legal_entity_name }}
          </li>
        </ul>
      </div>

      <!-- Step 2 form (hidden when affected entities panel is open) -->
      <div *ngIf="!showAffectedEntities()" class="modal-content-inner">
        <p class="modal-description">
          Please provide the contract that will be sent out and signed to validate the document
          change.
        </p>

        <!-- Adobe PDF upload zone -->
        <div class="upload-wrapper">
          <div
            *ngIf="!adobePdfFile() || adobePdfError()"
            class="upload-zone"
            data-testid="bcr-173-adobe-pdf-drop-zone"
            [class.upload-zone--dragging]="isDragging"
            [class.upload-zone--error]="adobePdfError()"
            (dragover)="onAdobePdfDragOver($event)"
            (dragleave)="onAdobePdfDragLeave($event)"
            (drop)="onAdobePdfDrop($event)"
            (click)="adobePdfInput.click()"
          >
            <span class="material-symbols-outlined upload-icon">upload_file</span>
            <p class="upload-text">
              Drag and drop file here or
              <label class="file-label" (click)="$event.stopPropagation()">
                <span class="choose-file-text">Choose File</span>
                <input
                  #adobePdfInput
                  tabindex="0"
                  type="file"
                  class="file-input"
                  data-testid="bcr-173-adobe-pdf-input"
                  accept=".pdf"
                  (change)="onAdobePdfSelected($event)"
                />
              </label>
            </p>
          </div>

          <div class="error-message" data-testid="bcr-173-adobe-pdf-error" *ngIf="adobePdfError()">
            <span class="material-symbols-outlined">error</span>
            <span>{{ adobePdfError() }}</span>
          </div>

          <div
            class="selected-file"
            data-testid="bcr-173-adobe-pdf-selected-file"
            *ngIf="adobePdfFile() && !adobePdfError()"
          >
            <div class="file-info">
              <span class="material-symbols-outlined file-icon">description</span>
              <div class="file-details">
                <span class="file-name">{{ adobePdfFile()?.name }}</span>
                <span class="file-size">{{ adobePdfFileSize }}</span>
              </div>
            </div>
            <button
              class="remove-file-btn"
              data-testid="bcr-173-adobe-pdf-remove-btn"
              (click)="removeAdobePdf()"
              type="button"
            >
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <div class="upload-info">
            <span class="upload-info-text">Supported format: .pdf</span>
            <span class="upload-info-text">Maximum size: 10MB</span>
          </div>
        </div>

        <!-- Recipient selection -->
        <p class="modal-description">
          Please select who you would like to notify of a signature request for the updated
          document.
        </p>
        <div class="recipient-group">
          <label class="recipient-option">
            <input
              type="radio"
              name="recipientGroup"
              data-testid="bcr-173-recipient-iga-only"
              [checked]="selectedRecipientGroup() === 'iga_coordinators'"
              (change)="onRecipientSelect('iga_coordinators')"
            />
            IGA Coordinators
          </label>
          <label class="recipient-option">
            <input
              type="radio"
              name="recipientGroup"
              data-testid="bcr-173-recipient-iga-and-contacts"
              [checked]="selectedRecipientGroup() === 'iga_coordinators_and_contacts'"
              (change)="onRecipientSelect('iga_coordinators_and_contacts')"
            />
            IGA Coordinators and Document Contact List
          </label>
        </div>

        <!-- Contact validation warning (kept for disabled-gate; banner above replaces text display) -->

        <div class="modal-actions">
          <app-az-button
            data-testid="bcr-173-back-btn"
            [label]="'Back'"
            [showIcon]="false"
            (click)="onBack()"
            variant="default"
            [disabled]="isUploading"
          ></app-az-button>
          <app-az-button
            data-testid="bcr-173-confirm-btn"
            [label]="'Confirm Contract'"
            [showIcon]="false"
            (click)="onNotifyConfirm()"
            variant="primary"
            [disabled]="confirmContractDisabled"
          ></app-az-button>
        </div>
      </div>
    </div>

    <!-- Step 3: Confirmation review -->
    <div
      class="modal-content overlay-scroll--custom"
      scrollActivity
      [scrollActivityCustom]="true"
      data-testid="bcr-173-confirmation-step"
      *ngIf="currentStep() === 3"
    >
      <div class="modal-content-inner">
        <p class="modal-description">
          You will be sending out the following documents to the {{ recipientGroupLabel }}:
        </p>

        <ul class="bcr-173-file-list">
          <li class="bcr-173-file-item" data-testid="bcr-173-confirm-replacement-file">
            <button
              type="button"
              class="bcr-173-file-download-btn"
              (click)="downloadFile(step1Data()?.file)"
            >
              <span class="file-name">{{ step1Data()?.file?.name }}</span>
              <span class="material-symbols-outlined file-icon">description</span>
            </button>
          </li>
          <li class="bcr-173-file-item" data-testid="bcr-173-confirm-adobe-pdf">
            <button
              type="button"
              class="bcr-173-file-download-btn"
              (click)="downloadFile(adobePdfFile())"
            >
              <span class="file-name">{{ adobePdfFile()?.name }}</span>
              <span class="material-symbols-outlined file-icon">description</span>
            </button>
          </li>
        </ul>

        <p class="modal-description">
          Are you sure you would like to upload and send these documents?
        </p>

        <div class="modal-actions">
          <app-az-button
            data-testid="bcr-173-step3-back-btn"
            [label]="'Back'"
            [showIcon]="false"
            (click)="onStep3Back()"
            variant="default"
            [disabled]="isUploading"
          ></app-az-button>
          <app-az-button
            data-testid="bcr-173-step3-confirm-btn"
            [label]="'Yes'"
            [showIcon]="false"
            (click)="onStep3Confirm()"
            variant="primary"
            [disabled]="isUploading"
          ></app-az-button>
        </div>
      </div>
    </div>
  </div>
</div>
